'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
// Type-only imports are erased at compile time, so they don't violate the
// 'server-only' guard on the dictionary modules.
import type { UiContent } from '@/lib/ui-content/ui-content';
import type { RegionUiContent } from '@/lib/ui-content/region-ui-content';

/**
 * Delivers the ACTIVE locale's UI strings to client components.
 *
 * The dictionaries in lib/ui-content/* hold every locale inline (~900KB raw)
 * and are marked 'server-only': importing them from a client component used to
 * ship all locales' strings to every visitor. Instead, the root layouts resolve
 * the current locale's slice server-side (via the existing getUiContent /
 * getRegionUiContent fallback logic) and pass it here — the browser receives
 * one locale's strings (~7KB gz) through the RSC payload, and hydration uses
 * the exact same object, so server and client markup always match.
 */
type UiContentContextValue = {
  ui: UiContent;
  region: RegionUiContent;
};

const UiContentContext = createContext<UiContentContextValue | null>(null);

export function UiContentProvider({
  ui,
  region,
  children,
}: UiContentContextValue & { children: ReactNode }) {
  const value = useMemo(() => ({ ui, region }), [ui, region]);
  return <UiContentContext.Provider value={value}>{children}</UiContentContext.Provider>;
}

function useUiContentContext(): UiContentContextValue {
  const ctx = useContext(UiContentContext);
  if (!ctx) {
    throw new Error(
      'useUiContent/useRegionUiContent must be rendered inside <UiContentProvider> (added by the root layouts).',
    );
  }
  return ctx;
}

/** The active locale's UI strings, resolved server-side by the root layout. */
export function useUiContent(): UiContent {
  return useUiContentContext().ui;
}

/** The active locale's region-selector strings (incl. region-specific earth emoji). */
export function useRegionUiContent(): RegionUiContent {
  return useUiContentContext().region;
}
